/**
 * Le classement et les fiches de joueur (GDD §18).
 *
 * **Seuls les comptes sécurisés y figurent.** Sans pseudonyme il n'y a personne
 * à nommer, et un compte anonyme ne se distingue pas d'un compte jetable créé
 * pour la façade. C'est du même coup la seule incitation honnête à sécuriser sa
 * partie — mieux qu'un rappel de plus.
 *
 * Les chiffres viennent du **miroir bridé** du serveur, jamais des sauvegardes :
 * un classement qui croirait le client sur parole n'aurait aucune valeur.
 */
import type { Ctx } from "../contexte.ts"
import { esc, nb, jauge, orJauge } from "../fmt.ts"
import { pseudoLocal } from "../../net/compte.ts"
import { TRIS, type LigneClassement, type Profil } from "../../net/classement.ts"
import { catalogue, pointsDe, libelleCategorie, totalPoints, type Categorie } from "../../sim/succes.ts"
import { icone, cleClasse } from "../sprites.ts"
import { item } from "../../sim/contenu.ts"
import { classe } from "../../sim/classes.ts"
import { SLOTS, LIBELLE_SLOT } from "../../sim/objets.ts"

/** Ce que le classement a chargé, tenu hors de l'état de jeu. */
export interface EtatClassement {
  tri: string
  joueurs: LigneClassement[] | null
  erreur: string | null
  /** pseudonyme dont la fiche est ouverte */
  ouvert: string | null
  profil: Profil | null
}

const COLONNE: Record<string, (l: LigneClassement) => string> = {
  points: (l) => `${nb(l.points)} pts`,
  niveau: (l) => `niv ${l.niveau}`,
  donjons: (l) => `${nb(l.donjons)} donjon${l.donjons > 1 ? "s" : ""}`,
  kills: (l) => `${nb(l.kills)} victoires`,
}

export function vueClassement(c: Ctx, e: EtatClassement): string {
  if (e.ouvert) return ficheJoueur(c, e)

  const moi = pseudoLocal()
  return `
  <section class="carte">
    <h2>Classement</h2>
    <p class="aide">Les chiffres sont ceux que le serveur a vérifiés, pas ceux
    que les navigateurs déclarent. ${moi ? "" : "Seules les parties sécurisées y figurent : sécurise la tienne depuis l'onglet Perso pour y apparaître."}</p>
    <nav class="filtres">${TRIS.map(([k, lib]) =>
      `<button data-tri-classement="${k}" aria-current="${k === e.tri}">${lib}</button>`).join("")}</nav>
  </section>

  <section class="carte">
    ${e.erreur ? `<p class="aide">${esc(e.erreur)}</p>`
      : !e.joueurs ? `<p class="aide">Chargement…</p>`
      : e.joueurs.length === 0 ? `<p class="aide">Personne n'a encore sécurisé sa partie. Sois le premier.</p>`
      : `<div class="rangs">${e.joueurs.map((l) => `
        <button class="rang ${l.pseudo === moi ? "moi" : ""}" data-joueur="${esc(l.pseudo)}">
          <span class="pos ${l.rang <= 3 ? `p${l.rang}` : ""}">${l.rang}</span>
          <span class="txt">
            <span class="nom">${esc(l.pseudo)}${l.pseudo === moi ? " <em>toi</em>" : ""}</span>
            <span class="det">niv ${l.niveau} · ${nb(l.kills)} vict. · ${nb(l.donjons)} donjon${l.donjons > 1 ? "s" : ""}</span>
          </span>
          <span class="val">${COLONNE[e.tri]?.(l) ?? ""}</span>
        </button>`).join("")}</div>`}
  </section>`
}

/**
 * Les personnages d'un joueur, et ce qu'ils portent.
 *
 * On ne montre ni l'inventaire ni le coffre : le serveur ne les publie même
 * pas. Ce qui a du sens à regarder chez quelqu'un, c'est son équipe et son
 * équipement — de quoi comprendre comment il joue, et se donner des idées.
 */
function vitrine(c: Ctx, p: Profil): string {
  const persos = p.vitrine ?? []
  if (persos.length === 0) {
    return `<section class="carte">
      <h2>Personnages</h2>
      <p class="aide">Rien à montrer pour l'instant : sa vitrine se remplira à sa prochaine sauvegarde.</p>
    </section>`
  }
  return `<section class="carte">
    <h2>Personnages <em>${persos.length}</em></h2>
    <div class="vitrines">${persos.map((x) => {
      const cl = classe(x.classe)
      const portees = SLOTS.map((sl) => ({ sl, e: x.equipement?.[sl] })).filter((y) => y.e)
      return `<div class="vitrine">
        <div class="tete">
          <span class="portrait" style="${icone("classes", cleClasse(x.classe), 44)}"></span>
          <div class="txt">
            <span class="nom">${esc(x.nom)}</span>
            <span class="det">${esc(cl.nom)} · niveau ${x.niveau} · ${nb(x.kills)} victoires</span>
          </div>
        </div>
        ${portees.length === 0
          ? `<p class="aide">Rien d'équipé.</p>`
          : `<div class="portees">${portees.map(({ sl, e }) => {
              const it = item(c.contenu, e!.item)
              return `<span class="portee" title="${esc(LIBELLE_SLOT[sl])} : ${esc(it?.nom ?? "?")}">
                <span class="ic" style="${icone("items", e!.item, 32)}"></span>
                <span class="nm">${esc(it?.nom ?? "?")}</span>
                <span class="lv">niv ${it?.niveau ?? "?"}</span>
              </span>`
            }).join("")}</div>`}
      </div>`
    }).join("")}</div>
  </section>`
}

/** La fiche d'un joueur : son palmarès et son équipe, rien de sa réserve. */
function ficheJoueur(c: Ctx, e: EtatClassement): string {
  const p = e.profil
  if (!p) {
    return `<section class="carte">
      <button class="retour" id="fermerProfil">‹ Retour au classement</button>
      <p class="aide">${e.erreur ? esc(e.erreur) : "Chargement…"}</p>
    </section>`
  }

  const tous = catalogue(c.contenu)
  const acquis = new Set(p.succes)
  const parCategorie = new Map<Categorie, { pris: number; total: number }>()
  for (const s of tous) {
    const v = parCategorie.get(s.categorie) ?? { pris: 0, total: 0 }
    v.total++
    if (acquis.has(s.cle)) v.pris++
    parCategorie.set(s.categorie, v)
  }
  // Les plus durs d'abord : c'est le palmarès, pas l'inventaire.
  const remarquables = tous.filter((s) => acquis.has(s.cle))
    .sort((a, b) => pointsDe(b) - pointsDe(a)).slice(0, 8)

  const jours = Math.max(1, Math.round((Date.now() - p.cree_le) / 86_400_000))

  return `
  <section class="carte">
    <button class="retour" id="fermerProfil">‹ Retour au classement</button>
    <h2>${esc(p.pseudo)}</h2>
    <p class="aide">Joue depuis ${jours} jour${jours > 1 ? "s" : ""}.</p>
    <div class="bilan">
      <div class="chiffre"><span>Niveau</span><b>${p.niveau}</b></div>
      <div class="chiffre"><span>Victoires</span><b>${nb(p.kills)}</b></div>
      <div class="chiffre"><span>Donjons</span><b>${nb(p.donjons)}</b></div>
      <div class="chiffre"><span>Personnages</span><b>${p.personnages}</b></div>
      <div class="chiffre"><span>Fortune</span><b>${nb(p.kamas)}</b></div>
      <div class="chiffre"><span>Points</span><b>${nb(p.points)}</b></div>
    </div>
  </section>

  ${vitrine(c, p)}

  <section class="carte">
    <h2>Succès <em>${p.succes.length} / ${tous.length}</em></h2>
    ${jauge(p.points / totalPoints(c.contenu), orJauge)}
    <div class="parcat">${[...parCategorie].map(([cat, v]) =>
      `<span class="cat ${v.pris === v.total ? "plein" : ""}">${esc(libelleCategorie(cat))}
        <b>${v.pris}/${v.total}</b></span>`).join("")}</div>
    ${remarquables.length === 0 ? `<p class="aide">Aucun succès pour l'instant.</p>`
      : `<p class="titrelot">Ses plus beaux</p>
      <div class="succes">${remarquables.map((s) => `
        <div class="succ fait">
          <span class="marque">★</span>
          <div class="txt"><span class="nom">${esc(s.nom)}</span><span class="det">${esc(s.detail)}</span></div>
          <span class="pts">+${pointsDe(s)}</span>
        </div>`).join("")}</div>`}
  </section>`
}
